import { PlusOutlined } from "@ant-design/icons";
import { Button, ConfigProvider, Flex } from "antd";
import { useEffect, useState } from "react";
import { translate } from "shared/languages";
import { colors } from "shared/theme/color";
import { ScheduleDraftsButton } from "../component/ScheduleDraftsDrawer";
import { MiniSchedule } from "./MiniCalendar";
import { ScheduleFilter } from "./ScheduleFilter";
import { useScheduleState } from "./ScheduleFilterContext";

// How tightly the panel packs, from the window height (the page never
// scrolls, so the panel has to fit the window): 0 as designed, 1 tighter
// spacing and a shorter month grid, 2 also smaller filter controls. Measured
// at the 87.5% zoom: level 0 needs a ~746px window, 1 ~642px, and 2 fits down
// to ~590px, below an 11-inch laptop's browser with its bookmarks bar
// (~630px); only a shorter window makes the panel scroll.
export type SiderDensity = 0 | 1 | 2;
const DENSITY_QUERIES = ["(max-height: 755px)", "(max-height: 650px)"];

const readDensity = (): SiderDensity =>
  DENSITY_QUERIES.filter((q) => window.matchMedia(q).matches)
    .length as SiderDensity;

// Read once before the first paint, so the panel never jumps on load; then
// follows the window as it is resized.
const useSiderDensity = (): SiderDensity => {
  const [density, setDensity] = useState<SiderDensity>(readDensity);
  useEffect(() => {
    const lists = DENSITY_QUERIES.map((q) => window.matchMedia(q));
    const update = () => setDensity(readDensity());
    lists.forEach((l) => l.addEventListener("change", update));
    return () => lists.forEach((l) => l.removeEventListener("change", update));
  }, []);
  return density;
};

export const ScheduleSider: React.FC = () => {
  const { setCreateDrawerOpen } = useScheduleState();
  const density = useSiderDensity();
  return (
    <div
      className="schedule-sider"
      data-density={density}
      style={{
        flexShrink: 0,
        width: 256,
        background: colors.card,
        border: `1px solid ${colors.line}`,
        borderRadius: 16,
        alignSelf: "flex-start",
        // The last resort, for a window shorter than the tightest level fits:
        // the panel scrolls inside itself so Reset Filter stays reachable.
        boxSizing: "border-box",
        maxHeight: "100%",
        overflowY: "auto",
        overscrollBehavior: "contain",
      }}
    >
      <div className="schedule-sider__stack">
        {/* The create button sits where its panel opens: the panel replaces
              this sider, so the pointer never crosses the page to reach it. */}
        <Flex gap={8}>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setCreateDrawerOpen(true)}
            style={{ flex: 1, height: 40 }}
          >
            {translate("schedule.new_schedule")}
          </Button>
          <ScheduleDraftsButton compact />
        </Flex>
        <MiniSchedule width={224} compact={density > 0} />
        <div style={{ height: 1, background: colors.line }} />
        {/* Only the filters get the small controls; the create button and
              the calendar keep their size at every level. */}
        <ConfigProvider componentSize={density === 2 ? "small" : undefined}>
          <ScheduleFilter />
        </ConfigProvider>
      </div>
    </div>
  );
};
